import { householdLoadingForceLayout } from './householdLoadingForceLayout';
import { COMPOSITION_CONFIG } from './householdLoadingGraph.config';
import {
  GraphCompositionEdge,
  GraphCurve,
  GraphEdge,
  GraphNode,
  GraphPoint,
  GraphRole,
  GraphSpace,
} from './householdLoadingGraph.types';

const {
  ROLE_RADIUS,
  MAX_VISIBLE_NODES,
  MAX_HUBS,
  MAX_CROSS_LINKS,
  WIDE_CONFIG,
  COMPACT_CONFIG,
  LABEL_GAP,
  LABEL_PADDING,
  GLYPH_WIDTH_RATIO,
  LINE_HEIGHT_RATIO,
  ASCENT_RATIO,
} = COMPOSITION_CONFIG;

export function householdLoadingNodeRadius(role: GraphRole) {
  return ROLE_RADIUS[role];
}

type CompositionConfig = typeof WIDE_CONFIG;

export type GraphLabelSide = 'right' | 'left' | 'above' | 'below';
export type GraphLabel = { lines: string[]; side: GraphLabelSide };
export type GraphEdgeLabel = { lines: string[] };

export type GraphComposition = {
  /** Unscaled force-layout positions, retained between extraction snapshots. */
  simulationPoints: ReadonlyMap<string, GraphPoint>;
  roles: ReadonlyMap<string, GraphRole>;
  points: ReadonlyMap<string, GraphPoint>;
  /** Real relationships between the shown items. A curve starts at `sourceID`. */
  edges: GraphCompositionEdge[];
  labels: ReadonlyMap<string, GraphLabel>;
  edgeLabels: ReadonlyMap<string, GraphEdgeLabel>;
  config: CompositionConfig;
};

/** A midpoint control keeps the shared quadratic animation path straight. */
export function householdLoadingCurveControl(
  a: GraphPoint,
  b: GraphPoint,
  _curve: GraphCurve,
  _center: GraphPoint
) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Point halfway along the quadratic curve from `a` to `b`. */
export function householdLoadingCurveMidpoint(
  a: GraphPoint,
  b: GraphPoint,
  control: GraphPoint
) {
  return {
    x: a.x * 0.25 + control.x * 0.5 + b.x * 0.25,
    y: a.y * 0.25 + control.y * 0.5 + b.y * 0.25,
  };
}

type Box = { left: number; right: number; top: number; bottom: number };

function overlaps(a: Box, b: Box) {
  return (
    a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top
  );
}

/** Wraps a name into at most `maxLines` lines. A cut last line ends with "…". */
export function householdLoadingLabelLines(
  name: string,
  maxChars: number,
  maxLines = 2
) {
  const lines: string[] = [];
  let current = '';
  const words = name.trim().split(/\s+/);
  for (const [index, word] of words.entries()) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars || !current) {
      current = next;
      continue;
    }
    if (lines.length === maxLines - 1) {
      current = `${current} ${words.slice(index).join(' ')}`;
      break;
    }
    lines.push(current);
    current = word;
  }
  lines.push(current);
  return lines.map((line) =>
    line.length > maxChars ? `${line.slice(0, maxChars - 1)}…` : line
  );
}

/**
 * Where a label sits relative to its node's center. `box` is the text bounds.
 * `x` and `baseline` place the first line of text; later lines follow at
 * `lineHeight` intervals.
 */
export function householdLoadingLabelGeometry({
  side,
  lines,
  radius,
  fontSize,
}: {
  side: GraphLabelSide;
  lines: readonly string[];
  radius: number;
  fontSize: number;
}) {
  const lineHeight = fontSize * LINE_HEIGHT_RATIO;
  const width =
    Math.max(...lines.map((line) => line.length)) *
    fontSize *
    GLYPH_WIDTH_RATIO;
  const height = lines.length * lineHeight;
  const offset = radius + LABEL_GAP;
  const anchors = {
    right: { left: offset, x: offset, textAnchor: 'start' },
    left: { left: -offset - width, x: -offset, textAnchor: 'end' },
    above: { left: -width / 2, x: 0, textAnchor: 'middle' },
    below: { left: -width / 2, x: 0, textAnchor: 'middle' },
  } as const;
  const { left, x, textAnchor } = anchors[side];
  const verticalTops = {
    right: -height / 2,
    left: -height / 2,
    above: -offset - height,
    below: offset,
  };
  const top = verticalTops[side];
  return {
    box: { left, right: left + width, top, bottom: top + height },
    x,
    textAnchor,
    baseline: top + fontSize * ASCENT_RATIO,
    lineHeight,
  };
}

/** Sides to try for a node, outward from the hub first. */
function labelSides(
  point: GraphPoint,
  center: GraphPoint,
  isHub: boolean
): GraphLabelSide[] {
  if (isHub) return ['below', 'above', 'right', 'left'];
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const horizontal: GraphLabelSide = dx >= 0 ? 'right' : 'left';
  const vertical: GraphLabelSide = dy >= 0 ? 'below' : 'above';
  const inwardHorizontal = horizontal === 'right' ? 'left' : 'right';
  const inwardVertical = vertical === 'below' ? 'above' : 'below';
  // Near the frame edge, a name may only fit on the inward-facing side.
  if (Math.abs(dx) >= Math.abs(dy) * 0.6) {
    return [horizontal, vertical, inwardVertical, inwardHorizontal];
  }
  return [vertical, horizontal, inwardHorizontal, inwardVertical];
}

/**
 * Chooses a small, calm set of real items and relationships to show, and
 * places them.
 *
 * A sparse forest of real relationships drives a repulsion-and-spring layout.
 * New snapshots reuse physical positions; the renderer eases all geometry
 * together toward the new layout instead of moving nodes and edges separately.
 */
export function householdLoadingGraphComposition(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  options: {
    space: GraphSpace;
    compact: boolean;
    previous?: GraphComposition;
  }
): GraphComposition {
  const { space, compact, previous } = options;
  const config = compact ? COMPACT_CONFIG : WIDE_CONFIG;
  const center = { x: space.width / 2, y: space.height / 2 };
  const order = new Map(nodes.map((node, index) => [node.id, index]));
  const validEdges = edges.filter(
    (edge) =>
      edge.sourceID !== edge.targetID &&
      order.has(edge.sourceID) &&
      order.has(edge.targetID)
  );
  const degrees = new Map(nodes.map((node) => [node.id, 0]));
  for (const edge of validEdges) {
    for (const id of [edge.sourceID, edge.targetID]) {
      degrees.set(id, degrees.get(id)! + 1);
    }
  }
  const degreeOf = (id: string) => degrees.get(id) ?? 0;
  const byConnection = (a: string, b: string) =>
    degreeOf(b) - degreeOf(a) || order.get(a)! - order.get(b)!;
  const visible = nodes
    .map((node) => node.id)
    .sort(
      (a, b) =>
        degreeOf(b) - degreeOf(a) ||
        Number(previous?.points.has(b)) - Number(previous?.points.has(a)) ||
        byConnection(a, b)
    )
    .slice(0, MAX_VISIBLE_NODES);
  const anchors = new Set(
    [...visible]
      .filter((id) => degreeOf(id) > 1)
      .sort(
        (a, b) =>
          degreeOf(b) - degreeOf(a) ||
          Number(previous?.roles.get(b) === 'hub') -
            Number(previous?.roles.get(a) === 'hub') ||
          byConnection(a, b)
      )
      .slice(0, MAX_HUBS)
  );
  const named = new Set(anchors);
  const namedCandidates = [...visible].sort(
    (a, b) =>
      Number(previous?.labels.has(b)) - Number(previous?.labels.has(a)) ||
      byConnection(a, b)
  );
  for (const id of namedCandidates) {
    if (named.size >= config.maxNamed) break;
    named.add(id);
  }
  const roles = new Map<string, GraphRole>();
  for (const id of visible) {
    if (anchors.has(id)) roles.set(id, 'hub');
    else roles.set(id, named.has(id) ? 'primary' : 'minor');
  }

  // A spanning forest preserves connectivity without drawing every relation.
  // Existing tree edges come first so new snapshots do not rebuild the tree.
  const shownEdges: GraphCompositionEdge[] = [];
  const cheapest = new Map<string, GraphEdge>();
  for (const edge of validEdges) {
    if (!roles.has(edge.sourceID) || !roles.has(edge.targetID)) continue;
    const pair = [edge.sourceID, edge.targetID].sort().join('\u0000');
    const known = cheapest.get(pair);
    if (!known || edge.id < known.id) cheapest.set(pair, edge);
  }
  const treeIDs = new Set(
    previous?.edges
      .filter((edge) => edge.curve === 'spoke')
      .map((edge) => edge.id)
  );
  const candidates = [...cheapest.values()].sort(
    (a, b) =>
      Number(treeIDs.has(b.id)) - Number(treeIDs.has(a.id)) ||
      degreeOf(b.sourceID) +
        degreeOf(b.targetID) -
        degreeOf(a.sourceID) -
        degreeOf(a.targetID) ||
      a.id.localeCompare(b.id)
  );
  const groups = new Map(visible.map((id) => [id, id]));
  const root = (id: string): string => {
    let current = id;
    while (groups.get(current) !== current) current = groups.get(current)!;
    return current;
  };
  const crossLinks: GraphEdge[] = [];
  for (const edge of candidates) {
    const source = root(edge.sourceID);
    const target = root(edge.targetID);
    if (source === target) {
      crossLinks.push(edge);
      continue;
    }
    groups.set(source, target);
    shownEdges.push({ ...edge, curve: 'spoke' });
  }
  for (const edge of crossLinks.slice(0, MAX_CROSS_LINKS)) {
    shownEdges.push({ ...edge, curve: 'rim' });
  }
  const { points, simulationPoints } = householdLoadingForceLayout(
    visible,
    shownEdges,
    roles,
    space,
    previous?.simulationPoints
  );
  // Name labels are placed on a free side of each node.
  const labels = new Map<string, GraphLabel>();
  const labeledNames = new Set<string>();
  const obstacles: Box[] = [];
  for (const [id, point] of points) {
    const radius = householdLoadingNodeRadius(roles.get(id)!) + 3;
    obstacles.push({
      left: point.x - radius,
      right: point.x + radius,
      top: point.y - radius,
      bottom: point.y + radius,
    });
  }
  const names = new Map(nodes.map((node) => [node.id, node.name.trim()]));
  const labelOrder = [...named].sort(
    (a, b) =>
      Number(anchors.has(b)) - Number(anchors.has(a)) ||
      Number(previous?.labels.has(b)) - Number(previous?.labels.has(a)) ||
      byConnection(a, b)
  );
  for (const id of labelOrder) {
    const name = names.get(id) ?? '';
    const point = points.get(id)!;
    // Duplicate names are separate factual nodes. Suppress only the repeated
    // display label so the graph keeps each identity and relationship.
    if (name === '' || labeledNames.has(name)) continue;
    const lines = householdLoadingLabelLines(
      name,
      anchors.has(id) ? config.hubLabelMaxChars : config.labelMaxChars,
      anchors.has(id) ? 2 : config.labelMaxLines
    );
    const sides = labelSides(point, center, anchors.has(id));
    const preferred = previous?.labels.get(id)?.side;
    const candidates = preferred ? [preferred, ...sides] : sides;
    for (const side of new Set(candidates)) {
      const { box: relative } = householdLoadingLabelGeometry({
        side,
        lines,
        radius: householdLoadingNodeRadius(roles.get(id)!),
        fontSize: config.labelFontSize + (anchors.has(id) ? 1 : 0),
      });
      const box = {
        left: point.x + relative.left - LABEL_PADDING,
        right: point.x + relative.right + LABEL_PADDING,
        top: point.y + relative.top - LABEL_PADDING / 2,
        bottom: point.y + relative.bottom + LABEL_PADDING / 2,
      };
      const inside =
        box.left >= 4 &&
        box.right <= space.width - 4 &&
        box.top >= 4 &&
        box.bottom <= space.height - 4;
      // A node's own dot touches its label gap, so only other boxes count.
      const clear = !obstacles.some(
        (other) =>
          overlaps(box, other) &&
          !(
            other.left < point.x &&
            other.right > point.x &&
            other.top < point.y &&
            other.bottom > point.y
          )
      );
      if (!inside || !clear) continue;
      obstacles.push(box);
      labels.set(id, { lines, side });
      labeledNames.add(name);
      break;
    }
  }

  // Relationship words: a very few, on curves that have room. One per kind.
  const edgeLabels = new Map<string, GraphEdgeLabel>();
  const usedKinds = new Set<string>();
  const edgeCandidates = shownEdges
    .filter((edge) => edge.kind.trim() !== '')
    .sort(
      (a, b) =>
        Number(previous?.edgeLabels.has(b.id)) -
          Number(previous?.edgeLabels.has(a.id)) ||
        Number(a.curve === 'rim') - Number(b.curve === 'rim') ||
        degreeOf(b.targetID) - degreeOf(a.targetID) ||
        a.id.localeCompare(b.id)
    );
  for (const edge of edgeCandidates) {
    if (edgeLabels.size >= config.maxEdgeLabels) break;
    const text = householdLoadingRelationshipText(edge.kind);
    if (usedKinds.has(text)) continue;
    const a = points.get(edge.sourceID)!;
    const b = points.get(edge.targetID)!;
    const control = householdLoadingCurveControl(a, b, edge.curve, center);
    const middle = householdLoadingCurveMidpoint(a, b, control);
    const lines = householdLoadingLabelLines(text, config.edgeLabelMaxChars, 1);
    const width =
      lines[0]!.length * config.edgeLabelFontSize * GLYPH_WIDTH_RATIO;
    const height = config.edgeLabelFontSize * LINE_HEIGHT_RATIO;
    const box = {
      left: middle.x - width / 2 - LABEL_PADDING,
      right: middle.x + width / 2 + LABEL_PADDING,
      top: middle.y - height / 2 - LABEL_PADDING,
      bottom: middle.y + height / 2 + LABEL_PADDING,
    };
    const inside =
      box.left >= 4 &&
      box.right <= space.width - 4 &&
      box.top >= 4 &&
      box.bottom <= space.height - 4;
    if (!inside || obstacles.some((other) => overlaps(box, other))) continue;
    obstacles.push(box);
    usedKinds.add(text);
    edgeLabels.set(edge.id, { lines });
  }

  return {
    simulationPoints,
    roles,
    points,
    edges: shownEdges,
    labels,
    edgeLabels,
    config,
  };
}

/** "successor_trustee_of" becomes "successor trustee of". */
export function householdLoadingRelationshipText(kind: string) {
  return kind.replace(/[_-]+/g, ' ').trim().toLowerCase();
}
